import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import Workflow, { ActionStatus, WorkflowAction, WorkflowCategoryOptions } from 'models/Workflow';
import { WorkflowActionIntent, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ChevronRight from 'svg/ChevronRight';

import {
  BENEFITS_ELIGIBILITY_KIND,
  eligibilityRollupStatus,
  EligibilityState,
  PAYER_SEARCH_KIND,
  readEligibility,
} from './eligibility/eligibilityData';
import { actionKindMeta, labelForValue } from './actionConfig';
import ExtractionResults from './ExtractionResults';
import FacilityDecisionPanel from './FacilityDecisionPanel';
import FanoutPanel, { fanoutCounts } from './FanoutPanel';
import PortalRetrievalBody from './PortalRetrievalBody';
import { ReferralModalSection } from './ReferralModal';
import ReferralOutcomePanel, { ReferralOutcomeResult } from './ReferralOutcomePanel';
import { categoryStatus, isQuietlyReady } from './referralProgress';
import { ActionStatusBadge, actionStatusDisplay, Pill, Tone } from './StatusBadge';
import StatusIcon, { indicatorColor, StatusIndicator, statusIndicator } from './StatusIcon';

interface ActionHandlers {
  // May return a promise (the page passes awaitable mutations) — bulk flows await for
  // ordered cache writes; single completions fire-and-forget.
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => unknown;
  // Amend a completed step's recorded outputs (change decision / add note) — the API
  // retires the prior record and completes a fresh instance atomically.
  onAmend: (actionId: string, outputs: WorkflowValuesInput) => unknown;
  isCompleting: boolean;
  // Stage outputs on the action without completing (save_progress) — the extension
  // hand-off parks retrieved values for the confirm panel.
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput) => void;
  isSavingProgress: boolean;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
  // Open the referral modal in edit mode focused on a section (documents / demographics).
  onEdit: (section: ReferralModalSection) => void;
  // Open the Insurance Eligibility Check slideout (payer select + verification result).
  onOpenEligibility: () => void;
}

// The Payer Search + Insurance Verification rows delegate their detail to the eligibility
// slideout, so the card shows only a one-line summary + a click-through.
export const ELIGIBILITY_KINDS = new Set([PAYER_SEARCH_KIND, BENEFITS_ELIGIBILITY_KIND]);

const ELIGIBILITY_SUMMARY: Record<EligibilityState, string> = {
  'select-payer': 'Select the patient’s payer to run the eligibility check',
  running: 'Running eligibility check…',
  'active-coverage': 'Active coverage found for this patient',
  'no-active-coverage': 'No active coverage found matching this patient’s provided insurance details',
  'payer-not-enrolled': 'This patient’s payer is not enrolled in Olio’s automated eligibility check',
  'no-results': 'No active coverage could be found matching this patient’s provided insurance details',
  error: 'Something went wrong while checking eligibility',
  pending: '',
};

// The single label + status shown for the collapsed eligibility row. Payer Search and Insurance
// Verification are two backend actions but one thing to the user, so they render as one row whose
// icon + status word derive from the *combined* eligibility state (not either action's raw status):
// e.g. "no active coverage" reads Flagged/orange even though BenefitsEligibility is technically
// completed. (OLIO-2301 — collapse per UX.)
export const ELIGIBILITY_LABEL = 'Benefit Verification';

export const ELIGIBILITY_STATUS: Record<EligibilityState, { indicator: StatusIndicator; label: string }> = {
  'select-payer': { indicator: 'review', label: 'Action Needed' },
  running: { indicator: 'running', label: 'Running' },
  'active-coverage': { indicator: 'complete', label: 'Completed' },
  'no-active-coverage': { indicator: 'attention', label: 'Flagged' },
  'no-results': { indicator: 'attention', label: 'Flagged' },
  'payer-not-enrolled': { indicator: 'attention', label: 'Flagged' },
  error: { indicator: 'error', label: 'Error' },
  pending: { indicator: 'pending', label: 'Pending' },
};

// Stable DOM ids so "waiting on" links can scroll to the blocking step.
export const categoryAnchor = (key: string) => `ir-category-${key}`;

// "a", "a and b", "a, b, and c" — same conjunction style as the WaitingOn joiner
// below. (Intl.ListFormat would do this but isn't in the project's ES2020 lib.)
function conjoin(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

interface Props extends ActionHandlers {
  category: WorkflowCategoryOptions;
  actions: WorkflowAction[];
  workflow: Workflow;
}

function actionLabel(action: WorkflowAction): string {
  return action.displayLabel;
}

// "4 SNFs" — the building count captioned with the buildings' type when they share one.
function buildingCount(workflow: Workflow, count: number): string {
  const typeName = workflow.groups[0]?.groupType?.displayName;
  return `${count} ${typeName ? `${typeName}s` : 'facilities'}`;
}

// Whether ActionBody renders anything for this action — banner, options/form, result, or a
// waiting reason. Terminal/in-flight states (canceled, skipped, running, completed with
// no result) have none, so a single-action category falls back to a status line.
function hasActionDetail(action: WorkflowAction): boolean {
  const meta = actionKindMeta(action.kind);
  if (action.stale || action.isFlagged || action.isPending) return true;
  // Actionable via option buttons, a kind-specific form (portal retrieval), or the
  // always-available Referral Outcome panel.
  if (
    action.isActionRequired &&
    (!!meta?.options || action.kind === 'RetrievePortalInformation' || action.kind === 'AcceptReferral')
  )
    return true;
  // Portal retrieval also renders after a failure (the manual form as recovery).
  if (action.kind === 'RetrievePortalInformation' && action.isErrored) return true;
  return (
    action.isCompleted &&
    (!!meta?.resultValueName ||
      !!meta?.resultSummaryValues ||
      action.kind === 'ExtractAttributes' ||
      action.kind === 'ProcessAttachments')
  );
}

// The category's headline status is a roll-up of its actions (design §12): a person
// needed anywhere wins, then done-when-all-done, else still going.

// One rendered row: either a normal per-action row, or the single collapsed eligibility row
// standing in for the Payer Search + Insurance Verification pair.
export type RowItem = { kind: 'action'; action: WorkflowAction } | { kind: 'eligibility' };

// Collapse the two eligibility actions into one row (at the position of the first), leaving every
// other action as its own row. Handles either eligibility action being absent.
export function buildRowItems(actions: WorkflowAction[]): RowItem[] {
  const items: RowItem[] = [];
  let eligibilityAdded = false;
  for (const action of actions) {
    if (ELIGIBILITY_KINDS.has(action.kind)) {
      if (!eligibilityAdded) {
        items.push({ kind: 'eligibility' });
        eligibilityAdded = true;
      }
      continue;
    }
    items.push({ kind: 'action', action });
  }
  return items;
}

// The header pill roll-up, with the eligibility pair folded into a single synthesized status so the
// pill agrees with the collapsed row (e.g. no-active-coverage → Flagged, not Completed). Non-
// eligibility categories (eligibilityStatus === undefined) fall straight through to categoryStatus.
function headerRollupStatus(actions: WorkflowAction[], eligibilityStatus: ActionStatus | undefined): ActionStatus {
  if (eligibilityStatus === undefined) return categoryStatus(actions);
  const others = actions.filter((a) => !ELIGIBILITY_KINDS.has(a.kind));
  // categoryStatus only reads these members; a synthesized stand-in for the eligibility pair keeps
  // its "every completed" semantics intact without leaking eligibility logic into referralProgress.
  const eligibilityStandIn = {
    status: eligibilityStatus,
    stale: false,
    isFlagged: eligibilityStatus === ActionStatus.FLAGGED,
    isActionRequired: eligibilityStatus === ActionStatus.ACTION_REQUIRED,
    isCompleted: eligibilityStatus === ActionStatus.COMPLETED,
  } as WorkflowAction;
  return categoryStatus([...others, eligibilityStandIn]);
}

export default function CategoryCard({ category, actions, workflow, ...handlers }: Props) {
  // A fanned-out kind (every instance building-stamped) collapses into one panel
  // (OLIO-2611); anything else renders as before. Categories can mix both.
  const byKind = new Map<string, WorkflowAction[]>();
  actions.forEach((action) => byKind.set(action.kind, [...(byKind.get(action.kind) ?? []), action]));
  const fannedKinds = Array.from(byKind.entries()).filter(([, siblings]) =>
    siblings.every((sibling) => sibling.groupId !== null)
  );
  const fannedIds = new Set(fannedKinds.flatMap(([, siblings]) => siblings.map((sibling) => sibling.id)));
  const plain = actions.filter((action) => !fannedIds.has(action.id));

  const single = actions.length === 1 && fannedKinds.length === 0;
  // Collapse the eligibility pair (Payer Search + Insurance Verification) among the plain,
  // non-fanned rows into one row (OLIO-2301).
  const rowItems = buildRowItems(plain);
  // That pair folded into one synthesized status (undefined when the category has no eligibility
  // actions) — drives the header pill and whether the card opens by default, so a "flagged"
  // eligibility result (whose underlying action is technically completed) is still shown.
  const eligibilityStatus = plain.some((a) => ELIGIBILITY_KINDS.has(a.kind))
    ? eligibilityRollupStatus(readEligibility(workflow).state)
    : undefined;
  // Open the sections that are active or need a person — running, ready-for-review,
  // flagged, or stale (incl. a flagged/action-needed eligibility result) — and leave done/pending collapsed.
  const [expanded, setExpanded] = useState(
    actions.some((a) => a.isActionRequired || a.isFlagged || a.stale || a.status === ActionStatus.RUNNING) ||
      eligibilityStatus === ActionStatus.FLAGGED ||
      eligibilityStatus === ActionStatus.ACTION_REQUIRED
  );

  const title = single ? actionLabel(actions[0]) : category.name;
  const onlyFanned = fannedKinds.length === 1 && plain.length === 0;
  // A lone fanned kind captions the header: Facility Decisions gets the result rollup
  // ("2 Accepted • 1 Denied"); other per-building kinds get the plain building count
  // ("4 SNFs", per the design). Otherwise list the rows, with the eligibility pair collapsed
  // to one "Insurance Eligibility Check" label.
  const subtitle = single
    ? null
    : onlyFanned
      ? fannedKinds[0][0] === 'FacilityDecision'
        ? fanoutCounts(workflow, fannedKinds[0][1], actionKindMeta('FacilityDecision'))
        : buildingCount(workflow, fannedKinds[0][1].length)
      : buildRowItems(actions)
          .map((item) => (item.kind === 'eligibility' ? ELIGIBILITY_LABEL : actionLabel(item.action)))
          .join(' • ');

  // Quiet-ready single step reads Ready for Review; otherwise the roll-up, with the eligibility
  // pair folded in so the pill agrees with the collapsed row (no-active-coverage → Flagged).
  const badgeStatus =
    single && isQuietlyReady(actions[0])
      ? ActionStatus.ACTION_REQUIRED
      : headerRollupStatus(actions, eligibilityStatus);

  return (
    <Card id={categoryAnchor(category.key)} data-testid={`category-card-${category.key}`}>
      <HeaderButton
        type='button'
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        $open={expanded}
        $tone={actionStatusDisplay(badgeStatus).tone}>
        <HeaderLeft>
          <Caret $open={expanded}>
            <ChevronRight width={12} height={12} />
          </Caret>
          <div>
            <CategoryTitle>{title}</CategoryTitle>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
        </HeaderLeft>
        <ActionStatusBadge status={badgeStatus} />
      </HeaderButton>

      {expanded &&
        (single ? (
          <SingleBody>
            {hasActionDetail(actions[0]) ? (
              <ActionBody action={actions[0]} workflow={workflow} {...handlers} />
            ) : (
              // No detail to show (e.g. canceled / running) — surface the status so the
              // body isn't empty, consistent with the rows in multi-action categories.
              <StatusOnly>
                <StatusIcon indicator={statusIndicator(actions[0])} size={16} />
                <RowTitle $color={indicatorColor(statusIndicator(actions[0]))}>
                  {actionStatusDisplay(actions[0].status).label}
                </RowTitle>
              </StatusOnly>
            )}
          </SingleBody>
        ) : (
          <>
            {fannedKinds.map(([kind, siblings]) =>
              kind === 'FacilityDecision' ? (
                <FacilityDecisionPanel
                  key={kind}
                  actions={siblings}
                  workflow={workflow}
                  onComplete={handlers.onComplete}
                  onAmend={handlers.onAmend}
                  onInvoke={handlers.onInvoke}
                  isCompleting={handlers.isCompleting || handlers.isInvoking}
                  // The panel's rows own the Accept/Deny buttons and show the decision
                  // as the row subtitle (per the Figma row layout) — ActionBody only
                  // contributes the banners/waiting states here.
                  renderRow={(action) => <ActionBody action={action} workflow={workflow} {...handlers} hideResult />}
                />
              ) : (
                <FanoutPanel
                  key={kind}
                  actions={siblings}
                  workflow={workflow}
                  blurb={siblings[0].description}
                  aside={kind === 'ClinicalReview' ? () => <AssessmentView /> : undefined}
                  renderRow={(action) => <ActionBody action={action} workflow={workflow} {...handlers} />}
                />
              )
            )}
            {plain.length > 0 && (
              <Rows>
                {rowItems.map((item) =>
                  // The collapsed eligibility row stands in for the Payer Search + Insurance
                  // Verification pair among the plain rows.
                  item.kind === 'eligibility' ? (
                    <EligibilityRow
                      key='eligibility'
                      workflow={workflow}
                      onOpenEligibility={handlers.onOpenEligibility}
                      onInvoke={handlers.onInvoke}
                      isInvoking={handlers.isInvoking}
                    />
                  ) : item.action.status === ActionStatus.SKIPPED ? (
                    <SkippedRow key={item.action.id} action={item.action} onEdit={handlers.onEdit} />
                  ) : (
                    <ActionRow
                      key={item.action.id}
                      data-testid='action-row'
                      data-action-kind={item.action.kind}
                      $flagged={item.action.isFlagged}>
                      <IconCell>
                        <StatusIcon indicator={statusIndicator(item.action)} size={16} />
                      </IconCell>
                      <RowMain>
                        <RowTitle $color={indicatorColor(statusIndicator(item.action))}>
                          {actionStatusDisplay(item.action.status).label}: {actionLabel(item.action)}
                        </RowTitle>
                        <ActionBody action={item.action} workflow={workflow} {...handlers} />
                      </RowMain>
                    </ActionRow>
                  )
                )}
              </Rows>
            )}
          </>
        ))}
    </Card>
  );
}

// The per-action content: its description, plus the controls/result/wait reason
// that depend on status. Reused for single- and multi-action categories.
function ActionBody({
  action,
  workflow,
  onComplete,
  isCompleting,
  onSaveProgress,
  isSavingProgress,
  onInvoke,
  isInvoking,
  onEdit,
  onOpenEligibility,
  hideResult = false,
}: { action: WorkflowAction; workflow: Workflow; hideResult?: boolean } & ActionHandlers) {
  const meta = actionKindMeta(action.kind);

  // Payer Search + Insurance Verification delegate their detail (payer select, results, errors)
  // to the eligibility slideout: show a one-line summary + a click-through, or the waiting reason
  // while still pending. The generic options/result blocks below never apply to these kinds.
  if (ELIGIBILITY_KINDS.has(action.kind)) {
    return (
      <>
        {action.stale && (
          <StaleBanner action={action} workflow={workflow} onInvoke={onInvoke} isInvoking={isInvoking} />
        )}
        {action.isPending ? (
          <WaitingOn action={action} workflow={workflow} />
        ) : (
          <EligibilitySummaryLink action={action} workflow={workflow} onOpen={onOpenEligibility} />
        )}
      </>
    );
  }

  return (
    <>
      {action.stale && <StaleBanner action={action} workflow={workflow} onInvoke={onInvoke} isInvoking={isInvoking} />}

      {action.isFlagged && <FlaggedPrompt action={action} workflow={workflow} onEdit={onEdit} />}

      {action.isCompleted &&
        !hideResult &&
        (action.kind === 'AcceptReferral' ? (
          <ReferralOutcomeResult action={action} workflow={workflow} />
        ) : (
          <CompletedResult action={action} workflow={workflow} />
        ))}

      {action.isCompleted && action.kind === 'ProcessAttachments' && (
        <CompletedNote>
          Documents have been successfully processed.{' '}
          <InlineLink type='button' onClick={() => onEdit('documents')} data-testid='add-documents'>
            Add additional documents
          </InlineLink>
        </CompletedNote>
      )}

      {action.isCompleted && action.kind === 'ExtractAttributes' && (
        <>
          <CompletedNote>
            Review extracted patient demographics below.{' '}
            <InlineLink type='button' onClick={() => onEdit('demographics')} data-testid='edit-demographics'>
              Edit demographics
            </InlineLink>
          </CompletedNote>
          <ExtractionResults action={action} workflow={workflow} />
        </>
      )}

      {action.isActionRequired && <MidRedoNotice action={action} workflow={workflow} />}

      {action.kind === 'AcceptReferral' && (action.isPending || action.isActionRequired) ? (
        // The Referral Outcome is always recordable (OLIO-2611) — its own panel renders
        // for pending AND action_required, instead of the generic options/waiting blocks.
        <ReferralOutcomePanel action={action} workflow={workflow} onComplete={onComplete} isCompleting={isCompleting} />
      ) : (
        <>
          {action.kind === 'RetrievePortalInformation' && (
            <PortalRetrievalBody
              action={action}
              workflow={workflow}
              onComplete={onComplete}
              isCompleting={isCompleting}
              onSaveProgress={onSaveProgress}
              isSavingProgress={isSavingProgress}
            />
          )}

          {action.isActionRequired && meta?.options && (
            <ActionRequired>
              {meta.prompt && <BodySmall>{meta.prompt}</BodySmall>}
              <Choices>
                {meta.options.map((option) => (
                  <Button
                    key={option.label}
                    variant={option.tone === 'danger' ? 'outline-danger' : 'primary'}
                    size='small'
                    disabled={isCompleting}
                    onClick={() => onComplete(action.id, option.outputs)}
                    data-testid={`action-option-${option.label}`}>
                    {option.label}
                  </Button>
                ))}
              </Choices>
            </ActionRequired>
          )}

          {action.isPending &&
            // The design explains a waiting step with its own description; the
            // blocker-linking WaitingOn is the fallback for steps without one.
            (action.description ? (
              <Muted>{action.description}</Muted>
            ) : (
              <WaitingOn action={action} workflow={workflow} />
            ))}
        </>
      )}
    </>
  );
}

// The single collapsed row standing in for the Payer Search + Insurance Verification pair. Its
// icon + status word come from the combined eligibility state; the body is the shared one-line
// summary + click-through to the slideout (or the waiting reason while still pending). Re-run /
// dismiss for a stale eligibility action stay available via the stale banner.
function EligibilityRow({
  workflow,
  onOpenEligibility,
  onInvoke,
  isInvoking,
}: {
  workflow: Workflow;
  onOpenEligibility: () => void;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  const { state, payerSearch, benefits } = readEligibility(workflow);
  const { indicator, label } = ELIGIBILITY_STATUS[state];
  const staleAction = [payerSearch, benefits].find((a) => a?.stale);
  const pendingAction = benefits ?? payerSearch;

  return (
    <ActionRow
      data-testid='eligibility-action-row'
      data-action-kind='InsuranceEligibilityCheck'
      $flagged={indicator === 'attention'}>
      <IconCell>
        <StatusIcon indicator={indicator} size={16} />
      </IconCell>
      <RowMain>
        <RowTitle $color={indicatorColor(indicator)}>
          {label}: {ELIGIBILITY_LABEL}
        </RowTitle>
        {staleAction && (
          <StaleBanner action={staleAction} workflow={workflow} onInvoke={onInvoke} isInvoking={isInvoking} />
        )}
        {state === 'pending' && pendingAction ? (
          <WaitingOn action={pendingAction} workflow={workflow} />
        ) : (
          <EligibilityLink type='button' onClick={onOpenEligibility} data-testid='open-eligibility'>
            <span>{ELIGIBILITY_SUMMARY[state] || 'View eligibility details'}</span>
            <ChevronRight width={12} height={12} />
          </EligibilityLink>
        )}
      </RowMain>
    </ActionRow>
  );
}

// Explain which step(s) a pending action is waiting on, linkable to that step.
function WaitingOn({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const blockers = blockersFor(action, workflow);

  if (blockers.length === 0) {
    return <Muted>Waiting on earlier steps before this can start.</Muted>;
  }

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Muted data-testid='waiting-on'>
      Waiting on{' '}
      {blockers.map((blocker, i) => (
        <span key={blocker.id}>
          {i > 0 && (i < blockers.length - 1 ? ', ' : blockers.length > 2 ? ', and ' : ' and ')}
          <StepLink type='button' onClick={() => scrollTo(blocker.id)}>
            {blocker.label}
          </StepLink>
        </span>
      ))}
      .
    </Muted>
  );
}

// The not-yet-completed producer action(s) of each missing required value — the
// step(s) that, once done, would unblock this action.
function blockersFor(action: WorkflowAction, workflow: Workflow): { id: string; label: string }[] {
  const missing = action.required.filter((name) => !workflow.valueFor(name, action.groupId)?.value);

  const byId = new Map<string, string>();
  missing.forEach((name) => {
    // The producer is whichever action declares it produces this value (API-provided
    // required/produces — no hardcoded kind→value map on the FE). Lane-scoped: a
    // per-building step is only ever unblocked by its own building's producer (or a
    // shared one) — never a sibling building's.
    const producer = workflow.actions.find(
      (a) => a.produces.includes(name) && (a.groupId === null || a.groupId === action.groupId)
    );
    if (producer && !producer.isCompleted) {
      byId.set(categoryAnchor(producer.categoryKey ?? ''), producer.displayLabel);
    }
  });

  return Array.from(byId, ([id, label]) => ({ id, label }));
}

function StaleBanner({
  action,
  workflow,
  onInvoke,
  isInvoking,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  const diff = workflow.inputDiffFor(action);

  return (
    <Banner data-testid='stale-banner'>
      <BannerTitle>Inputs changed since this ran</BannerTitle>
      {diff.length > 0 ? (
        <DiffList>
          {diff.map((d) => (
            <li key={d.name}>
              <DiffName>{labelForValue(workflow, d.name)}</DiffName>: <DiffOld>{d.ranWith || '—'}</DiffOld> →{' '}
              <DiffNew>{d.current || '—'}</DiffNew>
            </li>
          ))}
        </DiffList>
      ) : (
        // Trust the `stale` flag even when we can't enumerate the changed inputs
        // (empty snapshot, or a value the FE can't resolve) — don't over-claim.
        <Muted>Its inputs have changed since it ran.</Muted>
      )}
      <BannerActions>
        <Button
          size='small'
          disabled={isInvoking}
          onClick={() => onInvoke(action.id, 'rerun')}
          data-testid='stale-rerun'>
          Re-run
        </Button>
        <Button
          variant='outline-gray'
          size='small'
          disabled={isInvoking}
          onClick={() => onInvoke(action.id, 'dismiss')}
          data-testid='stale-dismiss'>
          Dismiss
        </Button>
      </BannerActions>
    </Banner>
  );
}

// The mid-redo awareness notice (design §9/§13): a producer of this step's inputs is
// in flight, so the information this step relies on may be about to change. Awareness,
// never a block — the person can still act (the engine never gates the decision).
function MidRedoNotice({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const producers = workflow.inFlightProducersFor(action);
  if (producers.length === 0) return null;

  return (
    <NoticeBanner data-testid='mid-redo-notice'>
      {conjoin(producers.map((producer) => producer.displayLabel))} {producers.length > 1 ? 'are' : 'is'} being redone
      and may change the information this step relies on. You can still act now.
    </NoticeBanner>
  );
}

// A flagged step can't run until a person supplies what the engine couldn't derive.
// Per the design it reads as instructional copy with the fix inline ("Add documents",
// "Manually add demographics") — no alarm box; the orange row title carries the state.
function FlaggedPrompt({
  action,
  workflow,
  onEdit,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onEdit: (section: ReferralModalSection) => void;
}) {
  const meta = actionKindMeta(action.kind);
  // The flag isn't a failure when nothing is actually missing downstream. A produced
  // value counts as outstanding only if it's absent AND some live step still requires
  // it — Extract also produces nice-to-haves (plan type, referral source) that no step
  // consumes, and their absence doesn't mean the person has anything left to add.
  // Contract-driven: `produces`/`required` come from the API.
  const liveConsumerNeeds = (name: string) =>
    !workflow.valueFor(name, action.groupId)?.value &&
    workflow.actions.some(
      (other) =>
        other.id !== action.id &&
        other.status !== ActionStatus.COMPLETED &&
        other.status !== ActionStatus.CANCELED &&
        other.status !== ActionStatus.SKIPPED &&
        other.required.includes(name) &&
        (other.groupId === null || action.groupId === null || other.groupId === action.groupId)
    );
  const outputsSatisfied = action.produces.length > 0 && !action.produces.some(liveConsumerNeeds);
  const names = action.unobtainableValues.map((name) => labelForValue(workflow, name));
  const copy =
    (outputsSatisfied && meta?.flaggedPromptSatisfied) ||
    meta?.flaggedPrompt ||
    (names.length > 0
      ? `This step needs ${conjoin(names)} to continue.`
      : 'This step needs more information before it can run.');
  const links = meta?.flaggedLinks?.filter((link) => !(outputsSatisfied && link.hideWhenSatisfied)) ?? [];

  return (
    <FlaggedCopy data-testid='flagged-prompt'>
      {copy}
      {links.map((link, i) => (
        <span key={link.label}>
          {i === 0 ? ' ' : ' or '}
          <InlineLink type='button' onClick={() => onEdit(link.section)} data-testid={`flagged-${link.section}`}>
            {link.label}
          </InlineLink>
        </span>
      ))}
    </FlaggedCopy>
  );
}

// A skipped (Not Applicable) step, Option C (OLIO-2633): name + gray chip + muted copy,
// and — when the step can still be fed later — the affordance that revives it (Add
// Documents opens the edit modal; the upload re-runs the parked lane engine-side).
function SkippedRow({ action, onEdit }: { action: WorkflowAction; onEdit: (section: ReferralModalSection) => void }) {
  const meta = actionKindMeta(action.kind);

  return (
    <ActionRow data-testid='action-row' data-action-kind={action.kind}>
      <RowMain>
        <SkippedHeader>
          <SkippedName>{action.displayLabel}</SkippedName>
          <Pill $tone='neutral' data-testid='skipped-chip'>
            Skipped
          </Pill>
        </SkippedHeader>
        {meta?.skippedCopy && <Muted>{meta.skippedCopy}</Muted>}
      </RowMain>
      {meta?.skippedAction && (
        <SkippedAside>
          <Button
            variant='outline-primary'
            size='small'
            onClick={() => onEdit(meta.skippedAction!.section)}
            data-testid='skipped-action'>
            {meta.skippedAction.label}
          </Button>
        </SkippedAside>
      )}
    </ActionRow>
  );
}

// The per-building assessment detail (Figma "Review Clinical Assessment"): a View link
// opening the assessment slideout. Bare minimum by design — the slideout is an empty
// shell until the AI clinical-assessment project fills it; this step never takes a
// decision.
function AssessmentView() {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <ViewLink type='button' onClick={() => setOpen(true)} data-testid='assessment-view'>
        View &rsaquo;
      </ViewLink>
      <Foldout open={open} setOpen={setOpen} closeOnBackdropClick data-testid='assessment-slideout'>
        <SlideoutBody />
      </Foldout>
    </div>
  );
}

// A one-line summary of the eligibility state + a click-through to open the slideout, where the
// payer select / full result / error detail lives. Shown on the Payer Search + Insurance
// Verification rows for every non-pending state.
function EligibilitySummaryLink({
  action,
  workflow,
  onOpen,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onOpen: () => void;
}) {
  const { state } = readEligibility(workflow);
  // The two rows share the derived state; while running, each row describes its own step.
  const summary =
    state === 'running' && action.kind === PAYER_SEARCH_KIND ? 'Running payer search…' : ELIGIBILITY_SUMMARY[state];

  return (
    <EligibilityLink type='button' onClick={onOpen} data-testid='open-eligibility'>
      <span>{summary || 'View eligibility details'}</span>
      <ChevronRight width={12} height={12} />
    </EligibilityLink>
  );
}

// Array values (the attachments packet) summarize as their filenames; scalars render as-is.
function formatSummaryValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((entry) =>
        entry && typeof entry === 'object' ? ((entry as { filename?: string }).filename ?? 'document') : String(entry)
      )
      .join(', ');
  }
  return String(value);
}

function CompletedResult({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const meta = actionKindMeta(action.kind);
  if (!meta) return null;

  // Read as unknown: integration-completed kinds (BenefitsEligibility) store a nested
  // object here, not the scalar the human-completed kinds write. Group-aware read: a
  // per-building step's result is ITS building's value.
  const value: unknown = meta.resultValueName ? workflow.valueFor(meta.resultValueName, action.groupId)?.value : null;
  const linkId = meta.resultLink ? workflow.valueFor(meta.resultLink.valueName)?.value : null;
  const summary = (meta.resultSummaryValues ?? [])
    .map((name) => ({ name, value: workflow.valueFor(name)?.value }))
    .filter((entry) => (Array.isArray(entry.value) ? entry.value.length > 0 : entry.value));
  if (value == null && summary.length === 0) return null;

  const rawLabel =
    value != null
      ? (meta.options?.find((o) => o.outputs[meta.resultValueName!] === value)?.label ??
        (typeof value === 'string' ? meta.resultLabels?.[value] : undefined) ??
        value)
      : null;
  // Defensive: only scalar labels reach JSX — never hand an unexpected object to React.
  const label = typeof rawLabel === 'string' ? rawLabel : null;

  return (
    <Result data-testid='action-result'>
      {label && (
        <>
          <ResultLabel>Result</ResultLabel>
          <ResultValue>
            {label}
            {linkId && meta.resultLink && (
              <ResultLink to={meta.resultLink.path(linkId)} data-testid='result-link'>
                {meta.resultLink.label} →
              </ResultLink>
            )}
          </ResultValue>
        </>
      )}
      {summary.map((entry) => (
        <SummaryRow key={entry.name} data-testid={`result-summary-${entry.name}`}>
          <SummaryName>{labelForValue(workflow, entry.name)}</SummaryName>
          <SummaryValue>{formatSummaryValue(entry.value)}</SummaryValue>
        </SummaryRow>
      ))}
    </Result>
  );
}

const Card = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
`;

// The header tints with the category's state (Figma): flagged reads warm, complete
// green, ready-for-review blue; everything else stays neutral.
const HEADER_TINTS: Partial<Record<Tone, string>> = {
  warning: colors.orange10,
  success: colors.green10,
  review: colors.blue10,
};

const HeaderButton = styled.button<{ $open: boolean; $tone: Tone }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${({ $tone }) => HEADER_TINTS[$tone] ?? colors.black3};
  border: none;
  border-bottom: ${({ $open }) => ($open ? `1px solid ${colors.black10}` : 'none')};
  cursor: pointer;
  text-align: left;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  > svg {
    flex-shrink: 0;
    margin-top: 4px;
    color: ${colors.black50};
  }
`;

// Single caret that rotates when the section is open — black in both states (the down
// chevron SVG is hardcoded blue, so we rotate the currentColor right chevron instead).
const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 4px;
  color: ${colors.black50};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.15s ease;
`;

const CategoryTitle = styled(BodySmallBold)`
  font-size: 15px;
`;

const Subtitle = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.black50};
`;

const SingleBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatusOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Rows = styled.div`
  background: ${colors.white};
`;

const ActionRow = styled.div<{ $flagged?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 16px 20px;
  background: ${({ $flagged }) => ($flagged ? colors.orange5 : 'transparent')};

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const IconCell = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  display: inline-flex;
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const RowTitle = styled(BodySmallBold)<{ $color?: string }>`
  color: ${({ $color }) => $color ?? colors.black100};
`;

const ActionRequired = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Choices = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const EligibilityLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font: inherit;
  font-weight: 600;

  > svg {
    flex-shrink: 0;
    color: ${colors.black50};
  }
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ResultLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const ResultValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
`;

const ResultLink = styled(Link)`
  margin-left: 8px;
  font-size: 13px;
  font-weight: 400;
  color: ${colors.blue100};
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
`;

const SummaryName = styled(BodySmall)`
  color: ${colors.black50};
  flex-shrink: 0;
`;

const SummaryValue = styled(BodySmall)`
  color: ${colors.black100};
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

const SkippedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SkippedName = styled(BodySmallBold)`
  font-size: 14px;
`;

const SkippedAside = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  align-self: center;
`;

const CompletedNote = styled(BodySmall)`
  display: block;
  color: ${colors.black75};
`;

const FlaggedCopy = styled(BodySmall)`
  display: block;
  color: ${colors.black75};
`;

const InlineLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font: inherit;
  font-weight: 500;
  text-decoration: underline;
`;

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${colors.orange50};
  background: ${colors.orange10};
`;

// Softer than Banner (orange = needs you): this is awareness only, so it reads as a
// note, not an alarm.
const NoticeBanner = styled(BodySmall)`
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${colors.black10};
  background: ${colors.black3};
  color: ${colors.black75};
`;

const BannerTitle = styled(BodySmallBold)`
  color: ${colors.black100};
`;

const DiffList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: ${colors.black75};
`;

const DiffName = styled.span`
  font-weight: 500;
`;

const DiffOld = styled.span`
  color: ${colors.black50};
  text-decoration: line-through;
`;

const DiffNew = styled.span`
  font-weight: 500;
  color: ${colors.black100};
`;

const BannerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const SlideoutBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
`;

const StepLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  cursor: pointer;
  text-decoration: underline;
`;

// The assessment View link: same reset as StepLink, sized down, no underline (Figma).
const ViewLink = styled(StepLink)`
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
`;
